import { useRef, type ReactNode } from "react";
import {
  AlertTriangle,
  ArrowRight,
  ExternalLink,
  FileText,
  Rocket,
  Sparkles,
  Target,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import {
  buildRequestFormulatorCommand,
  getHostLabel,
  getLaunchCommand,
} from "@/lib/chat/cli-guidance";
import { useScrollSpotlight } from "../scroll-spotlight";
import type { HelpStepComponentProps } from "../help-flows";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives (matching cli-setup-steps pattern)                      */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
          {step}
        </div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
        {step}
      </div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({
  icon: Icon,
  title,
  description,
  accentClass,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  accentClass: string;
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={cn("h-5 w-5", accentClass)} />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1 — What is an Epic Request?                                         */
/* -------------------------------------------------------------------------- */

export function WhatIsRequestStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="What even is an Epic Request?">
        <p className="text-sm leading-7 text-muted-foreground">
          An <strong className="text-foreground/90">Epic Request</strong> is where every project in
          SpecTree begins. {"It's"} not just an idea on paper — {"it's"} the first step in a
          full workflow that goes from <strong className="text-foreground/90">your idea</strong> →{" "}
          <strong className="text-foreground/90">structured Epic Request</strong> →{" "}
          <strong className="text-foreground/90">features & tasks</strong> →{" "}
          <strong className="text-foreground/90">execution</strong>.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          SpecTree walks you through a short interview and produces a{" "}
          <strong className="text-foreground/90">structured Epic Request</strong> containing
          your <strong className="text-foreground/90">problem statement</strong>,{" "}
          <strong className="text-foreground/90">proposed solution</strong>,{" "}
          <strong className="text-foreground/90">impact assessment</strong>, and{" "}
          <strong className="text-foreground/90">success criteria</strong>. Once you approve
          it, the Planner turns that request into actionable features and tasks.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="What goes into one?">
        <p className="text-sm leading-7 text-muted-foreground">
          You only need to do one thing — describe what you want. {"Don't"} overthink it. SpecTree handles
          the rest:
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <InfoCard
            icon={Target}
            title="Your Idea"
            description="What do you want to build, fix, or improve? A new feature, a problem you've spotted, an improvement — anything goes."
            accentClass="text-blue-400"
          />
          <InfoCard
            icon={Sparkles}
            title="SpecTree Does the Rest"
            description="SpecTree walks you through a structured interview, then produces a complete Epic Request with your problem statement, proposed solution, impact assessment, and success criteria."
            accentClass="text-emerald-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="What happens after you submit it?">
        <p className="text-sm leading-7 text-muted-foreground">
          After SpecTree creates your request, it lands in <strong className="text-foreground/90">Pending</strong> status.
          You <strong className="text-foreground/90">approve it yourself</strong> — no waiting on
          anyone else. Once approved, the Planner agent turns it into a real plan with features,
          tasks, and a clear order of work.
        </p>
        <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-5 py-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="h-4 w-4 text-blue-400" />
            <span>Your idea</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="h-4 w-4 text-emerald-400" />
            <span>SpecTree formulates</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Target className="h-4 w-4 text-amber-400" />
            <span>You approve</span>
          </div>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2 — Launch the Request Formulator                                    */
/* -------------------------------------------------------------------------- */

export function CopyRequestCommandStep() {
  const host = useResolvedHost();
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title={`Open your ${getHostLabel(host)}`}>
        <p className="text-sm leading-7 text-muted-foreground">
          You should already have this running from the setup guide. If {"it's"} not open, go to your
          terminal and type:
        </p>
        <CopyableTerminal title="Terminal" copyText={getLaunchCommand(host)}>
          <p>
            <span className="text-foreground/45">$ </span>
            <span className="text-foreground">{getLaunchCommand(host)}</span>
          </p>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Working on a code project? Go to that folder first">
        <p className="text-sm leading-7 text-muted-foreground">
          <strong className="text-foreground/90">This only applies if your request is about a codebase or a project that already has files.</strong>{" "}
          If {"you're"} just sharing a general idea with no existing code, skip to the next step.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          The Request Formulator looks at your current folder to check if your project has been
          documented. So you need to tell {getHostLabel(host)} to go to your project folder first. Just paste
          the path like this:
        </p>
        <CopyableTerminal title={getHostLabel(host)} copyText="cd /Users/yourname/projects/my-app">
          <div className="space-y-1.5">
            <p>
              <span className="text-blue-400/70">{">"} </span>
              <span className="text-foreground">cd /Users/yourname/projects/my-app</span>
            </p>
            <p className="text-emerald-400/70">✓ Changed directory to /Users/yourname/projects/my-app</p>
          </div>
        </CopyableTerminal>
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/[0.04] p-4">
          <p className="text-sm leading-7 text-muted-foreground">
            <strong className="text-foreground/90">{"Don't"} know your project path?</strong> Open your project
            folder in Finder (Mac) or File Explorer (Windows), then copy the path from the address
            bar and paste it after <code className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs text-foreground/80">cd</code>.
          </p>
        </div>
      </NumberedInstruction>

      <NumberedInstruction step={3} title="Launch the Request Formulator and describe your idea">
        <p className="text-sm leading-7 text-muted-foreground">
          {"There's"} no agent list to pick from. Paste this one command into {getHostLabel(host)} and
          press Enter — it starts the guided interview right in the session {"you're"} already in.
          Swap the text inside the quotes for what you want to build, fix, or improve:
        </p>
        <CopyableTerminal
          title={getHostLabel(host)}
          copyText={buildRequestFormulatorCommand(undefined, host)}
        >
          <p>
            <span className="text-blue-400/70">{">"} </span>
            <span className="text-foreground">{buildRequestFormulatorCommand(undefined, host)}</span>
          </p>
        </CopyableTerminal>
        <p className="text-sm leading-7 text-muted-foreground">
          A real one looks like this — a brand-new feature idea, a problem {"you've"} noticed, or
          something you want to make better:
        </p>
        <CopyableTerminal
          title={`${getHostLabel(host)} — Request Formulator`}
          copyText="I want to build a guided onboarding experience that walks new users through setup step by step"
        >
          <div className="space-y-1.5">
            <p>
              <span className="text-blue-400/70">{">"} </span>
              <span className="text-foreground">I want to build a guided onboarding experience that walks new users through setup step by step</span>
            </p>
          </div>
        </CopyableTerminal>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400">
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Describe what you want to do</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Share an outline of your idea, problem, or feature — whatever {"you're"} trying to
                build or fix. {"Don't"} worry about covering everything — the Formulator will ask
                follow-up questions if it needs more detail.
              </p>
            </div>
          </div>
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={4} title="Handle the documentation check">
        <p className="text-sm leading-7 text-muted-foreground">
          Right after you type your idea, the Formulator checks if your project has been
          through a documentation audit. This is so SpecTree can fully understand your codebase
          before getting started — the better it understands your project, the more accurate
          and useful the results will be.
        </p>
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm font-semibold text-foreground">{'"This project hasn\'t been through a documentation audit yet."'}</p>
              <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                {"Don't"} panic! This just means SpecTree {"hasn't"} scanned and documented your
                codebase yet. A doc audit lets SpecTree deeply understand your project so it can
                give you much better results. {"You'll"} see three choices:
              </p>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <DocAuditOption
            number="1"
            title="Create a Documentation Audit request"
            description="This is always recommended. SpecTree will scan your entire codebase and generate thorough documentation so SpecTree can understand your project inside and out. This leads to far better and more accurate results."
            recommended
          />
          <DocAuditOption
            number="2"
            title={"I've already been through a SpecTree doc audit"}
            description={"Pick this if SpecTree has already scanned and documented this project before. This tells the Formulator to skip the check since your project is already understood."}
          />
          <DocAuditOption
            number="3"
            title="Skip for now and create a different request"
            description={"Pick this if you want to skip for now. You'll see this check again next time — but we really recommend doing the audit when you can."}
          />
        </div>
        <p className="text-sm leading-7 text-muted-foreground">
          Once you pick an option, the Formulator moves on to the interview — asking follow-up questions about your idea, the impact, and who benefits — one topic at a time.
        </p>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 3 — Walk through the interview                                       */
/* -------------------------------------------------------------------------- */

export function FollowPromptsStep({ onAdvance, onNavigateToFlow }: Pick<HelpStepComponentProps, "onAdvance" | "onNavigateToFlow">) {
  const host = useResolvedHost();
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Walk through the interview">
        <p className="text-sm leading-7 text-muted-foreground">
          The Formulator walks you through a friendly conversation — one topic at a time. Just
          answer naturally in your own words. {"Here's"} exactly what {"it'll"} ask you about:
        </p>
        <div className="space-y-3">
          <InterviewStage
            number="1"
            title="Problem Statement"
            question="What problem are you trying to solve? What gap exists in the current system? Who is experiencing this problem?"
            example={"I want to build a step-by-step onboarding guide so new users don't get lost during setup."}
          />
          <InterviewStage
            number="2"
            title="Proposed Solution"
            question="What do you propose building? How would this solve the problem? What's the high-level approach?"
            example="A step-by-step wizard that guides new users through each part of the setup process."
          />
          <InterviewStage
            number="3"
            title="Impact Assessment"
            question="Who will benefit from this? What are the expected benefits? How will this improve the system or workflow?"
            example="Every new user — onboarding time drops from 30 minutes to 5."
          />
          <InterviewStage
            number="4"
            title="Success Criteria"
            question="What specific metrics would you track? How would we know this is working? (optional — but helpful)"
            example="90% of new users complete setup without asking for help."
            optional
          />
          <InterviewStage
            number="5"
            title="Target Audience"
            question="Which users or teams will use this? Are there specific personas or use cases? (optional)"
            example="New developers joining the team for the first time."
            optional
          />
          <InterviewStage
            number="6"
            title="Technical Context"
            question="Are there alternative approaches you considered? Dependencies or prerequisites? Rough effort estimate? (optional)"
            example={"Needs to work on mobile too. Probably 2\u20133 weeks of work."}
            optional
          />
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400">
              <Zap className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{"Don't"} stress about the optional ones</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                If you {"don't"} have an answer, just say so and the Formulator will move on.
                The first three (Your Idea, What You Envision, Impact) are the most important.
              </p>
            </div>
          </div>
        </div>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Review what was submitted">
        <p className="text-sm leading-7 text-muted-foreground">
          After the interview, the Formulator submits your Epic Request right away and shows
          you a full preview of what it filed. Give it a quick read-through:
        </p>
        <div className="space-y-3">
          <ReviewCheck title="Does the problem sound right?">
            Make sure it captures what {"you're"} actually trying to solve.
          </ReviewCheck>
          <ReviewCheck title="Does SpecTree's plan look right?">
            SpecTree generates a solution based on your input — make sure it captured what you meant.
          </ReviewCheck>
          <ReviewCheck title="Want to change anything?">
            Just say what to change — the Formulator updates the request in place.
          </ReviewCheck>
        </div>
        <p className="text-sm leading-7 text-muted-foreground">
          Right after submitting, the Formulator runs a{" "}
          <strong className="text-foreground/90">compliance audit</strong> automatically.
          {"You'll"} see a score out of 100 covering dimensions like Title Quality, Problem Depth,
          Solution Depth, and Impact Clarity.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          <strong className="text-foreground/90">Aim for 90+</strong> before moving on. If your
          score is lower, head back to {getHostLabel(host)} and ask it to help improve your Epic Request — then
          hit <strong className="text-foreground/90">Re-audit</strong> on the request page to check again.
          You can re-audit as many times as you need.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={3} title="Approve your own request">
        <p className="text-sm leading-7 text-muted-foreground">
          After submission, your Epic Request has a status of{" "}
          <strong className="text-foreground/90">Pending</strong>. Before the Planner agent can turn
          it into a real plan, <strong className="text-foreground/90">you need to approve it yourself</strong>.
          {"Here's"} how:
        </p>
        <div className="space-y-3">
          <NavStep
            number="1"
            title="Go to SpecTree"
            description={<>Open <strong className="text-foreground/90">spectree.toro.com</strong> in your browser and log in.</>}
          >
            <a
              href="https://spectree.toro.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white/[0.1]"
            >
              Open SpecTree
              <ExternalLink className="h-4 w-4" />
            </a>
          </NavStep>
          <NavStep
            number="2"
            title="Find Epic Requests"
            description={<>Click <strong className="text-foreground/90">Requests</strong> in the bottom dock. {"You'll"} see a list of all requests.</>}
          />
          <NavStep
            number="3"
            title="Open your request"
            description={<>Find the one you just created — it will have a <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs font-semibold text-amber-400">Pending</span> badge. Click on it to open the full details.</>}
          />
          <NavStep
            number="4"
            title="Review the details"
            description={<>{"You'll"} see your idea, the SpecTree-generated solution, impact assessment, and all the details the Formulator put together.</>}
          />
          <NavStep
            number="5"
            title="Check the audit score"
            description={<>Every Epic Request is automatically audited and given a compliance score out of 100. Look for the score breakdown — it covers Title Quality, Problem Depth, Solution Depth, Impact Clarity, and more. <strong className="text-foreground/90">Aim for 90+ before approving.</strong> If your score is low, go back to {getHostLabel(host)} and ask it to help improve your request. You can re-audit any time.</>}
          />
          <NavStep
            number="6"
            title="Approve it"
            description={<>Once {"you're"} happy with the audit score, click the <strong className="text-foreground/90">Approve</strong> button. The status changes from <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs font-semibold text-amber-400">Pending</span> to <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-xs font-semibold text-emerald-400">Approved</span>. {"That's"} it — {"it's"} now ready for planning.</>}
          />
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={4} title={"What's next?"}>
        <p className="text-sm leading-7 text-muted-foreground">
          {"You've"} created and approved your Epic Request. Now {"it's"} time to turn it into a real
          plan. {"Here's"} where to go from here:
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <InfoCard
            icon={Sparkles}
            title="Continue to Plan an Epic"
            description={"Head to the Plan an Epic guide (the next guide in the Help Center). The Planner agent will take your approved request and turn it into a full epic with features and tasks."}
            accentClass="text-emerald-400"
          />
          <InfoCard
            icon={FileText}
            title="Track it on spectree.toro.com"
            description={"Your request and epic are always visible at spectree.toro.com. You can check the status, read comments, and see the full history anytime."}
            accentClass="text-blue-400"
          />
        </div>
        <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.06] to-primary/[0.03] p-6">
          <div className="flex items-start gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-primary/20 bg-primary/15 text-primary">
              <Rocket className="h-7 w-7" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                {"What's next"}
              </p>
              <h3 className="mt-2 text-xl font-semibold text-foreground">
                Time to plan your epic
              </h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                Your Epic Request is ready. The next step is the <strong className="text-foreground/90">Planner</strong> —
                it takes your approved request and breaks it down into a full epic with features, tasks, and
                acceptance criteria. Just give it your Epic Request ID or name.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => onNavigateToFlow?.("plan-epic")}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  <Rocket className="h-4 w-4" />
                  Continue to Plan an Epic
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onAdvance?.()}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.04] px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white/[0.08]"
                >
                  Back to Help Center
                </button>
              </div>
            </div>
          </div>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* ── Helper components ── */



function DocAuditOption({
  number,
  title,
  description,
  recommended,
}: {
  number: string;
  title: string;
  description: string;
  recommended?: boolean;
}) {
  return (
    <div className={cn(
      "rounded-xl border p-4",
      recommended
        ? "border-emerald-500/20 bg-emerald-500/[0.04]"
        : "border-white/[0.06] bg-white/[0.03]",
    )}>
      <div className="flex items-start gap-3">
        <div className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold",
          recommended
            ? "bg-emerald-500/15 text-emerald-400"
            : "bg-white/[0.06] text-muted-foreground",
        )}>
          {number}
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">
            {title}
            {recommended && (
              <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-400">
                Recommended
              </span>
            )}
          </p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p>
        </div>
      </div>
    </div>
  );
}

function InterviewStage({
  number,
  title,
  question,
  example,
  optional,
}: {
  number: string;
  title: string;
  question: string;
  example: string;
  optional?: boolean;
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-xs font-bold text-blue-400">
          {number}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">
            {title}
            {optional && (
              <span className="ml-2 text-xs font-normal text-muted-foreground/60">optional</span>
            )}
          </p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">{question}</p>
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-blue-500/10 bg-blue-500/[0.03] px-3 py-2">
            <span className="mt-0.5 text-xs font-medium text-blue-400/60">e.g.</span>
            <p className="text-sm italic text-foreground/60">{example}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewCheck({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-xs font-bold text-emerald-400">✓</span>
      <div>
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}

function NavStep({
  number,
  title,
  description,
  children,
}: {
  number: string;
  title: string;
  description: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-xs font-bold text-blue-400">
        {number}
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-sm leading-6 text-muted-foreground">{description}</p>
        {children}
      </div>
    </div>
  );
}
